import "./data-list-cell.ts";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Div } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { CellEntry, type Option, RowId } from "@tento-lona/sheets";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { RowLocalId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { DataListCell } from "./data-list-cell.ts";
import { CellExpander } from "../../shared/cell-expand/cell-expander.ts";
import { CellExpandPopup } from "../../shared/cell-expand/cell-expand-popup.ts";

@component({
  name: "std-data-plugin-cell",
})
export class DataPluginCell extends LonaWebComponent {
  #$loading: HTMLElement;
  #$text = this.hydrate<DataListCell>("text");
  #$expandBtn: HTMLElement = this.$("expand-btn");

  #handle: Option<SheetDelegate> = null;
  #position: Option<{
    column: ColumnIndex.Key.Resolved.Default;
    rowId: RowId;
    localId: RowLocalId;
  }> = null;
  #cells: CellEntry[] = [];
  #customs: CellEntry.Timepoint<string>[] = [];

  /** Owns the expand button, x-axis-clip detection, and the full-width popup.
   *  Content here is a read-only data list — the popup inherits that. */
  #expander: CellExpander = new CellExpander({
    host: this,
    $expandBtn: this.#$expandBtn,
    getAnchor: () => this.#anchor(),
    isClipped: () => this.#$text.hasHorizontalOverflow(),
    makePopup: (ctx) => this.#makePopup(ctx),
    popupIdPrefix: "data-json-popup",
  });

  constructor() {
    super();
    this.#$loading = Div.make();
    this.#$loading.classList.add("loading");
    this.$("root").appendChild(this.#$loading);
  }

  /** Set the cell's sheet anchor so the expand popup can align to this row. */
  bindAnchor(anchor: CellExpander.Anchor): void {
    this.#handle = anchor.handle;
    this.#position = {
      column: anchor.column,
      rowId: anchor.rowId,
      localId: anchor.localId,
    };
    this.#expander.onRebind();
  }

  bindLoading() {
    this.#$loading.style.display = "block";
    this.#$text.style.display = "none";
    this.#expander.refresh();
  }

  bindJson(
    obj: CellEntry[],
    customs: CellEntry.Timepoint<string>[],
  ) {
    this.#cells = obj;
    this.#customs = customs;
    this.#$loading.style.display = "none";
    this.#$text.style.display = "block";
    this.#$text.bind(obj, customs);
    this.#expander.refresh();
  }

  /** Re-check expandability — called by the plugin's `onResize`. */
  refreshExpand(): void {
    this.#expander.refresh();
  }

  #anchor(): Option<CellExpander.Anchor> {
    if (this.#handle == null || this.#position == null) return null;
    return {
      handle: this.#handle,
      column: this.#position.column,
      rowId: this.#position.rowId,
      localId: this.#position.localId,
    };
  }

  async #makePopup(
    ctx: CellExpander.PopupContext,
  ): Promise<CellExpander.Popup> {
    const $list = DataListCell.make();
    $list.bindExpanded(this.#cells, this.#customs);

    const $popup = CellExpandPopup.make();
    $popup.bind({
      heightPx: ctx.heightPx,
      background: "var(--bg-surface-light)",
      $content: $list,
      onWaitForLayout: () => CellExpandPopup.waitForElementLayout($list),
      onFocus: () => {},
      onRecycleContent: () => {},
      onDismiss: ctx.requestDismiss,
    });
    return $popup;
  }

  onRecycle(): void {
    super.onRecycle();
    this.#expander.onRecycle();
    this.#handle = null;
    this.#position = null;
    this.#cells = [];
    this.#customs = [];
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        height: var(--height);
      }

      #root {
        height: 100%;
        position: relative;
      }

      .loading {
        display: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      #text {
        display: none;
      }

      #expand-btn {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 20px;
        height: 20px;
        display: grid;
        place-items: center;
        border-radius: 4px;
        cursor: pointer;
        z-index: 1;
        background: var(--bg-hover-opaque-very-light);
        color: var(--fg-icon-pri);
      }

      #expand-btn > svg {
        width: 12px;
        height: 12px;
      }

      #expand-btn:hover {
        background: var(--bg-hover-opaque);
      }

      #expand-btn[hidden] {
        display: none;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <std-data-list-cell id=text></std-data-list-cell>
      <div id="expand-btn" hidden></div>
    </std-col>
  `;
}
